import { useMemo } from 'react';

import { useFetch } from 'src/hooks/use-fetch';

import axiosInstance from 'src/utils/axios';

// ----------------------------------------------------------------------

/**
 * Default response transformer for list endpoints
 * @param {Object} data - Raw response data
 * @returns {Object} Normalized response with results and count
 */
const defaultListTransformer = (data) => data || { results: [], count: 0 };

/**
 * Default response transformer for single item endpoints
 * @param {Object} data - Raw response data
 * @returns {Object} Raw data or empty object
 */
const defaultItemTransformer = (data) => data || null;

/**
 * Default response transformer for array endpoints
 * @param {Array} data - Raw response data
 * @returns {Array} Raw data or empty array
 */
const defaultArrayTransformer = (data) => data || [];

/**
 * Creates a standardized data fetching hook with consistent return structure
 *
 * @param {string} url - API endpoint URL
 * @param {Object} options - Configuration options
 * @param {Function} options.transformer - Function to transform response data (default: identity)
 * @param {Function} options.emptyCheck - Function to check if data is empty (default: checks results length)
 * @returns {Function} Hook that returns {data, loading, error, validating, empty, refresh}
 */
export function createFetchHook(url, options = {}) {
    const {
        transformer = (data) => data,
        emptyCheck = (data, isLoading) => !isLoading && (!data || data?.results?.length === 0 || data?.count === 0)
    } = options;

    return function useFetchData(args) {
        const { refresh, data, isLoading, error, isValidating } = useFetch(url, args);

        const memoizedValue = useMemo(
            () => ({
                data: transformer(data),
                loading: isLoading,
                error,
                validating: isValidating,
                empty: emptyCheck(data, isLoading),
                refresh
            }),
            [data, error, isLoading, isValidating, refresh]
        );

        return memoizedValue;
    };
}

/**
 * Creates a standardized data fetching hook for object endpoints
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @param {Object} options - Configuration options
 * @returns {Function} Hook for fetching object data
 */
export function createObjectHook(baseUrl, options = {}) {
    return createFetchHook(baseUrl, {
        transformer: defaultItemTransformer,
        emptyCheck: (data, isLoading) => !isLoading && (!data || Object.keys(data).length === 0),
        ...options
    });
}

/**
 * Creates a standardized data fetching hook for list endpoints
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @param {Object} options - Configuration options
 * @returns {Function} Hook for fetching list data
 */
export function createListHook(baseUrl, options = {}) {
    return createFetchHook(baseUrl, {
        transformer: defaultListTransformer,
        emptyCheck: (data, isLoading) => !isLoading && (!data || data?.count === 0),
        ...options
    });
}

/**
 * Creates a standardized data fetching hook for single item endpoints
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @param {Object} options - Configuration options
 * @returns {Function} Hook for fetching single item data
 */
export function createGetHook(baseUrl, options = {}) {
    return function useGetItem(id, args) {
        const url = id ? `${baseUrl}/${id}` : null;

        return createFetchHook(url, {
            transformer: defaultItemTransformer,
            emptyCheck: (data, isLoading) => !isLoading && (!data || !data?.id),
            ...options
        })(args);
    };
}

/**
 * Creates a standardized data fetching hook for array endpoints
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @param {Object} options - Configuration options
 * @returns {Function} Hook for fetching array data
 */
export function createArrayHook(baseUrl, options = {}) {
    return createFetchHook(baseUrl, {
        transformer: defaultArrayTransformer,
        emptyCheck: (data, isLoading) => !isLoading && (!data || data?.length === 0),
        ...options
    });
}

/**
 * Creates a standardized count hook
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @returns {Function} Hook for fetching count data
 */
export function createCountHook(baseUrl) {
    const url = `${baseUrl}/count`;

    return function useCount(args) {
        const { data, isLoading, error, isValidating, refresh } = useFetch(url, args);

        const memoizedValue = useMemo(
            () => ({
                data: data || { all: 0 },
                loading: isLoading,
                error,
                validating: isValidating,
                empty: !isLoading && !data,
                refresh
            }),
            [data, error, isLoading, isValidating]
        );

        return memoizedValue;
    };
}

// ----------------------------------------------------------------------
// Mutation Actions
// ----------------------------------------------------------------------

/**
 * Creates a standardized create action
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @param {Object} options - Configuration options
 * @param {Function} options.payloadTransformer - Transform data before sending
 * @returns {Function} Create function
 */
export function createCreateAction(baseUrl, options = {}) {
    const { payloadTransformer = (data) => data } = options;

    return function create(data) {
        const url = baseUrl;
        const payload = payloadTransformer(data);
        return axiosInstance.post(url, payload);
    };
}

/**
 * Creates a standardized update action
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @param {Object} options - Configuration options
 * @param {Function} options.payloadTransformer - Transform data before sending
 * @param {string} options.idField - Field name for the ID (default: 'id')
 * @returns {Function} Update function
 */
export function createUpdateAction(baseUrl, options = {}) {
    const {
        payloadTransformer = (data) => data,
        idField = 'id'
    } = options;

    return function update(data) {
        const url = `${baseUrl}/${data[idField]}`;
        const payload = payloadTransformer(data);
        return axiosInstance.put(url, payload);
    };
}

/**
 * Creates a standardized partial update action (PATCH)
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @param {Object} options - Configuration options
 * @param {Function} options.payloadTransformer - Transform data before sending
 * @param {string} options.idField - Field name for the ID (default: 'id')
 * @returns {Function} Partial update function
 */
export function createPartialUpdateAction(baseUrl, options = {}) {
    const {
        payloadTransformer = (data) => data,
        idField = 'id'
    } = options;

    return function partialUpdate(data) {
        const url = idField ? `${baseUrl}/${data[idField]}` : baseUrl;
        const payload = payloadTransformer(data);
        return axiosInstance.patch(url, payload);
    };
}

/**
 * Creates a standardized delete action
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @returns {Function} Delete function
 */
export function createDeleteAction(baseUrl) {
    return function deleteItem(id) {
        const url = `${baseUrl}/${id}`;
        return axiosInstance.delete(url);
    };
}

/**
 * Creates a standardized upsert action
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @param {Object} options - Configuration options
 * @returns {Function} Upsert function
 */
export function createUpsertAction(baseUrl, options = {}) {
    const { payloadTransformer = (data) => data, idField = 'id' } = options;

    // use either create or update based on presence of id
    return function upsert(data) {
        if (data[idField]) {
            const url = `${baseUrl}/${data[idField]}`;
            const payload = payloadTransformer(data);
            return axiosInstance.put(url, payload);
        } else {
            const url = baseUrl;
            const payload = payloadTransformer(data);
            return axiosInstance.post(url, payload);
        }
    };
}

/**
 * Creates a standardized bulk delete action
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @returns {Function} Bulk delete function
 */
export function createBulkDeleteAction(baseUrl) {
    return function bulkDelete(ids) {
        const url = `${baseUrl}/bulk-delete`;
        return axiosInstance.delete(url, { data: { ids } });
    };
}

// ----------------------------------------------------------------------
// Action Factory
// ----------------------------------------------------------------------

/**
 * Creates a complete CRUD action set for a resource
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @param {Object} options - Configuration options
 * @param {boolean} options.includePartialUpdate - Include partial update action (default: true)
 * @param {Function} options.payloadTransformer - Transform payloads for mutations
 * @param {Function} options.listTransformer - Transform list response data
 * @param {Function} options.itemTransformer - Transform single item response data
 * @param {string} options.idField - Field name for the ID in update operations (default: 'id')
 * @param {Object} options.customActions - Additional custom actions
 * @returns {Object} Complete action set {list, get, create, update, partialUpdate?, delete, count?, ...customActions}
 */
export function createResourceActions(baseUrl, options = {}) {
    const {
        payloadTransformer,
        listTransformer,
        itemTransformer,
        idField,
        customActions = {}
    } = options;

    const actions = {
        list: createListHook(baseUrl, listTransformer ? { transformer: listTransformer } : {}),
        get: createGetHook(baseUrl, itemTransformer ? { transformer: itemTransformer } : {}),
        partialUpdate: createPartialUpdateAction(baseUrl, { payloadTransformer, idField }),
        create: createCreateAction(baseUrl, { payloadTransformer }),
        update: createUpdateAction(baseUrl, { payloadTransformer, idField }),
        upsert: createUpsertAction(baseUrl, { payloadTransformer, idField }),
        delete: createDeleteAction(baseUrl),
        bulkDelete: createBulkDeleteAction(baseUrl),
        count: createCountHook(baseUrl)
    };

    // Add custom actions
    Object.entries(customActions).forEach(([key, value]) => {
        actions[key] = value;
    });

    return actions;
}

/**
 * Creates a class-based action wrapper for better organization
 *
 * @param {string} baseUrl - Base API endpoint URL
 * @param {Object} options - Same options as createResourceActions
 * @returns {Class} Action class with all CRUD methods
 */
export function createActionClass(baseUrl, options = {}) {
    const actions = createResourceActions(baseUrl, options);

    return class ResourceActions {
        static url = baseUrl;

        static list = actions.list;
        static get = actions.get;
        static create = actions.create;
        static update = actions.update;
        static partialUpdate = actions.partialUpdate;
        static delete = actions.delete;
        static count = actions.count;
        static upsert = actions.upsert;

        // Add custom actions as static methods
        constructor() {
            Object.entries(options.customActions || {}).forEach(([key, value]) => {
                ResourceActions[key] = value;
            });
        }
    };
}
